// src/features/license/ActivateLicenseDialog.tsx
// Activate this platform's license: online (ActivateLicense — verifies with
// the master) or air-gapped (InputLicenseKey — verified offline against the
// embedded public key). Same key, two verification paths.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useLicense, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, AutoGrowTextarea, SegmentedControl } from '@/components/form';
import { useScope } from '@/hooks/useScope';
import type { License } from '@core/types/license';
import { currentHardwareFingerprint, errMessage, makeEntity } from './format';

interface ActivateLicenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (license: License) => void;
}

export const ActivateLicenseDialog = observer(({ open, onClose, onSaved }: ActivateLicenseDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLicense();

  const [mode, setMode] = useState<'online' | 'offline'>('online');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setKey('');
    setMode('online');
  }, [open]);

  const submit = async () => {
    if (!key.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const fingerprint = currentHardwareFingerprint();
      const res =
        mode === 'online'
          ? await store.ActivateLicense({ entity: makeEntity('', 'license', scope), license_key: key.trim(), hardware_fingerprint: fingerprint })
          : await store.InputLicenseKey({ entity: makeEntity('', 'license', scope), license_key: key.trim(), hardware_fingerprint: fingerprint });
      onSaved(res.license!);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('license.activate.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('license.activate.action')}
      canSubmit={!!key.trim()}
      isSubmitting={isSaving}
      dirty={!!key.trim()}
      width="md"
      aria-label={t('license.activate.title')}
    >
      <div className="space-y-3 px-3.5 pb-2 pt-3">
        {error && <Alert variant="error">{error}</Alert>}
        <SegmentedControl
          aria-label={t('license.activate.title')}
          value={mode}
          onChange={(v) => setMode(v as 'online' | 'offline')}
          options={[
            { value: 'online', label: t('license.activate.online') },
            { value: 'offline', label: t('license.activate.offline') },
          ]}
        />
        <p className="text-sm text-muted-foreground">
          {mode === 'online' ? t('license.activate.onlineHint') : t('license.activate.offlineHint')}
        </p>
        <AutoGrowTextarea
          value={key}
          onChange={setKey}
          minRows={3}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          aria-label={t('license.activate.licenseKey')}
          className="font-mono"
        />
      </div>
    </FormDialog>
  );
});
